"use client";

import { useCallback, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";

import { Button } from "@/components/ui/button";
import { Note } from "./layout";
import { NoteDropdown } from "./note-dropdown";
import { Icon } from "@/components/ui/icon";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { MentionEditor } from "./mention-editor";
import { MentionText } from "./mention-text";

export type NoteLikeAuthor = {
  id?: string;
  email?: string;
  first_name?: string | null;
  last_name?: string | null;
  avatar_url?: string | null;
};

export type NoteLike = {
  id: string;
  author: NoteLikeAuthor | null;
  author_user_id?: string | null;
  content: string;
  depth: number;
  like_count: number;
  dislike_count: number;
  viewer_reaction: "like" | "dislike" | null;
  replies: NoteLike[];
  created_at: string;
};

const ICON_ENTER = {
  initial: { scale: 0.25, opacity: 0, filter: "blur(4px)" },
  animate: { scale: 1, opacity: 1, filter: "blur(0px)" },
  transition: { type: "spring" as const, duration: 0.3, bounce: 0 },
};

const REPLY_ANIM = {
  initial: { opacity: 0, y: 8 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: 4 },
  transition: { type: "spring" as const, duration: 0.25, bounce: 0 },
};

function formatTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function authorDisplayName(author: NoteLikeAuthor | null): string {
  if (!author) return "Unknown";
  const name = `${author.first_name ?? ""} ${author.last_name ?? ""}`.trim();
  return name || "Unknown";
}

function AuthorAvatar({
  author,
  size = 18,
}: {
  author: NoteLikeAuthor | null;
  size?: number;
}) {
  const initials = author
    ? `${author.first_name?.[0] ?? ""}${author.last_name?.[0] ?? ""}`.toUpperCase()
    : "?";

  return (
    <Avatar
      className="rounded-[3.375px] shadow-[0px_0.45px_0.9px_0px_rgba(0,0,0,0.05)]"
      style={{ width: size, height: size }}
    >
      <AvatarImage src={author?.avatar_url ?? undefined} alt="" />
      <AvatarFallback
        className="bg-primary/10 dark:bg-[#0D111A] font-medium text-primary dark:text-[#2C7EFF]"
        style={{ fontSize: size * 0.45 }}
      >
        {initials || "?"}
      </AvatarFallback>
    </Avatar>
  );
}

function ReplyInput({
  onSubmit,
  onCancel,
  isPending,
}: {
  onSubmit: (content: string) => void;
  onCancel: () => void;
  isPending: boolean;
}) {
  const [value, setValue] = useState("");
  const handleSubmit = () => {
    const trimmed = value.trim();
    if (!trimmed) return;
    onSubmit(trimmed);
    setValue("");
  };

  return (
    <div className="flex w-full flex-col gap-2 items-start">
      <div className="relative w-full overflow-hidden rounded-lg border border-border shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-lg bg-contrast/6"
        />
        <div className="relative">
          <MentionEditor
            placeholder="Write reply..."
            onChange={setValue}
            onSubmit={handleSubmit}
            onCancel={onCancel}
            autoFocus
          />
        </div>
        <div className="pointer-events-none absolute inset-0 rounded-[inherit] shadow-[inset_0px_-1px_0px_0px_rgba(0,0,0,0.08)]" />
      </div>
      <div className="flex h-7 w-full items-center justify-end gap-2.5">
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={handleSubmit}
          disabled={isPending || !value.trim()}
        >
          Send
        </Button>
      </div>
    </div>
  );
}

function EditNoteInput({
  initialContent,
  onSave,
  onCancel,
  isPending,
}: {
  initialContent: string;
  onSave: (content: string) => void;
  onCancel: () => void;
  isPending: boolean;
}) {
  const [value, setValue] = useState(initialContent);
  const handleSave = () => {
    const trimmed = value.trim();
    if (!trimmed || trimmed === initialContent) {
      onCancel();
      return;
    }
    onSave(trimmed);
  };

  return (
    <div className="flex w-full flex-col gap-2 items-start">
      <div className="relative w-full overflow-hidden rounded-lg border border-border shadow-[0px_1px_2px_0px_rgba(0,0,0,0.05)]">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 rounded-lg bg-contrast/6"
        />
        <div className="relative">
          <MentionEditor
            initialContent={initialContent}
            onChange={setValue}
            onSubmit={handleSave}
            onCancel={onCancel}
            autoFocus
            minHeight={48}
          />
        </div>
        <div className="pointer-events-none absolute inset-0 rounded-[inherit] shadow-[inset_0px_-1px_0px_0px_rgba(0,0,0,0.08)]" />
      </div>
      <div className="flex h-7 w-full items-center justify-end gap-2.5">
        <Button variant="ghost" size="sm" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          variant="secondary"
          size="sm"
          onClick={handleSave}
          disabled={isPending || !value.trim()}
        >
          Save
        </Button>
      </div>
    </div>
  );
}

function NoteToolbar({
  note,
  canReply = true,
  canEdit,
  canWrite,
  busy = false,
  onReply,
  onToggleLike,
  onToggleDislike,
  onEdit,
  onDelete,
}: {
  note: NoteLike;
  canReply?: boolean;
  canEdit: boolean;
  canWrite: boolean;
  busy?: boolean;
  onReply: () => void;
  onToggleLike: () => void;
  onToggleDislike: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}) {
  const [likeKey, setLikeKey] = useState(0);
  const [dislikeKey, setDislikeKey] = useState(0);

  if (!canWrite) return null;

  return (
    <Note.ActionsRow>
      {canReply && (
        <Note.ReplyButton
          onClick={onReply}
          disabled={busy}
          className="-m-2.5 p-2.5 sm:m-0 sm:p-0"
        />
      )}
      <div className="flex items-center gap-5 sm:gap-3">
        <div className="flex items-start gap-5 sm:gap-3">
          <motion.button
            type="button"
            disabled={busy}
            onClick={() => {
              setLikeKey((k) => k + 1);
              onToggleLike();
            }}
            className="flex items-center gap-1 -m-2.5 p-2.5 sm:m-0 sm:p-0 transition-colors disabled:pointer-events-none disabled:opacity-50"
            whileTap={{ scale: 0.96 }}
            transition={{ type: "spring", duration: 0.15, bounce: 0 }}
          >
            <div className="opacity-[0.65]">
              <motion.div key={likeKey} {...ICON_ENTER}>
                <Icon
                  name="IconThumbsUp"
                  size={16}
                  className={cn(
                    "text-foreground",
                    note.viewer_reaction === "like" &&
                      "text-primary dark:text-[#2C7EFF]",
                  )}
                />
              </motion.div>
            </div>
            <AnimatePresence mode="wait" initial={false}>
              {note.like_count > 0 && (
                <motion.span
                  key={note.like_count}
                  className="text-[13px] font-normal tabular-nums text-foreground/70"
                  initial={{ opacity: 0, y: -4 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 4 }}
                  transition={{ type: "spring", duration: 0.2, bounce: 0 }}
                >
                  {note.like_count}
                </motion.span>
              )}
            </AnimatePresence>
          </motion.button>
          <motion.button
            type="button"
            disabled={busy}
            onClick={() => {
              setDislikeKey((k) => k + 1);
              onToggleDislike();
            }}
            className="-m-2.5 p-2.5 sm:m-0 sm:p-0 disabled:pointer-events-none disabled:opacity-50"
            whileTap={{ scale: 0.96 }}
            transition={{ type: "spring", duration: 0.15, bounce: 0 }}
          >
            <div className="opacity-[0.65]">
              <motion.div key={dislikeKey} {...ICON_ENTER}>
                <Icon
                  name="IconThumbsDown"
                  size={16}
                  className={cn(
                    "text-foreground",
                    note.viewer_reaction === "dislike" &&
                      "text-destructive dark:text-red-400",
                  )}
                />
              </motion.div>
            </div>
          </motion.button>
        </div>
        <NoteDropdown canEdit={canEdit} onEdit={onEdit} onDelete={onDelete} />
      </div>
    </Note.ActionsRow>
  );
}

const MAX_REPLY_DEPTH = 2;

export type NoteCardProps = {
  note: NoteLike;
  canEdit: boolean;
  canWrite: boolean;
  /** Used to compute canEdit for nested replies. */
  currentUserId?: string | null;
  isOrgAdmin?: boolean;
  viaLabel?: string | null;
  busy?: boolean;
  editPending?: boolean;
  onReply: (parent: NoteLike, content: string) => void;
  onEdit: (
    note: NoteLike,
    content: string,
    options?: { onSuccess?: () => void },
  ) => void;
  onDelete: (note: NoteLike) => void;
  onReact: (note: NoteLike, value: "like" | "dislike") => void;
  onClearReaction: (note: NoteLike) => void;
};

function replyCanEdit(
  reply: NoteLike,
  currentUserId: string | null | undefined,
  isOrgAdmin: boolean,
): boolean {
  return (
    (!!currentUserId && reply.author_user_id === currentUserId) || isOrgAdmin
  );
}

function ReplyCard({
  note,
  canWrite,
  currentUserId,
  isOrgAdmin = false,
  viaLabel,
  busy = false,
  editPending = false,
  onReply,
  onEdit,
  onDelete,
  onReact,
  onClearReaction,
}: Omit<NoteCardProps, "canEdit">) {
  const [showReply, setShowReply] = useState(false);
  const [editing, setEditing] = useState(false);
  const canEdit = replyCanEdit(note, currentUserId, isOrgAdmin);
  const canReply = canWrite && note.depth < MAX_REPLY_DEPTH;
  const isOptimistic = note.id.startsWith("temp-");

  const handleReply = useCallback(
    (content: string) => {
      setShowReply(false);
      onReply(note, content);
    },
    [onReply, note],
  );

  const handleEdit = useCallback(
    (content: string) => {
      onEdit(note, content, { onSuccess: () => setEditing(false) });
    },
    [onEdit, note],
  );

  const handleToggleLike = useCallback(() => {
    if (note.viewer_reaction === "like") onClearReaction(note);
    else onReact(note, "like");
  }, [note, onReact, onClearReaction]);

  const handleToggleDislike = useCallback(() => {
    if (note.viewer_reaction === "dislike") onClearReaction(note);
    else onReact(note, "dislike");
  }, [note, onReact, onClearReaction]);

  const subReplies = note.replies ?? [];

  return (
    <Note.Container
      variant="reply"
      className={isOptimistic ? "pointer-events-none opacity-60" : undefined}
    >
      <Note.Header>
        <div className="flex items-center gap-1.5">
          <AuthorAvatar author={note.author} size={18} />
          <span className="text-sm font-medium text-foreground">
            {authorDisplayName(note.author)}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          {viaLabel && (
            <span className="inline-flex items-center rounded bg-contrast/8 px-1.5 py-0.5 text-[11px] leading-4 text-contrast/60">
              via {viaLabel}
            </span>
          )}
          <Note.Timestamp>{formatTime(note.created_at)}</Note.Timestamp>
        </div>
      </Note.Header>
      {editing && canEdit ? (
        <EditNoteInput
          initialContent={note.content}
          onSave={handleEdit}
          onCancel={() => setEditing(false)}
          isPending={editPending}
        />
      ) : (
        <Note.Content>
          <MentionText content={note.content} />
        </Note.Content>
      )}
      {!editing && (
        <NoteToolbar
          note={note}
          canReply={canReply}
          canEdit={canEdit}
          canWrite={canWrite}
          busy={busy}
          onReply={() => setShowReply((p) => !p)}
          onToggleLike={handleToggleLike}
          onToggleDislike={handleToggleDislike}
          onEdit={canEdit ? () => setEditing(true) : undefined}
          onDelete={canEdit ? () => onDelete(note) : undefined}
        />
      )}

      {subReplies.length > 0 && !editing && (
        <div className="flex flex-col gap-2">
          {subReplies.map((reply) => (
            <ReplyCard
              key={reply.id}
              note={reply}
              canWrite={canWrite}
              currentUserId={currentUserId}
              isOrgAdmin={isOrgAdmin}
              viaLabel={viaLabel}
              busy={busy}
              editPending={editPending}
              onReply={onReply}
              onEdit={onEdit}
              onDelete={onDelete}
              onReact={onReact}
              onClearReaction={onClearReaction}
            />
          ))}
        </div>
      )}

      <AnimatePresence>
        {canReply && showReply && !editing && (
          <motion.div key="reply-input" {...REPLY_ANIM}>
            <ReplyInput
              onSubmit={handleReply}
              onCancel={() => setShowReply(false)}
              isPending={busy}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </Note.Container>
  );
}

export function NoteCard({
  note,
  canEdit,
  canWrite,
  currentUserId,
  isOrgAdmin = false,
  viaLabel,
  busy = false,
  editPending = false,
  onReply,
  onEdit,
  onDelete,
  onReact,
  onClearReaction,
}: NoteCardProps) {
  const [showReply, setShowReply] = useState(false);
  const [showAllReplies, setShowAllReplies] = useState(false);
  const [editing, setEditing] = useState(false);
  const isOptimistic = note.id.startsWith("temp-");

  const handleReply = useCallback(
    (content: string) => {
      setShowReply(false);
      onReply(note, content);
    },
    [onReply, note],
  );

  const handleEdit = useCallback(
    (content: string) => {
      onEdit(note, content, { onSuccess: () => setEditing(false) });
    },
    [onEdit, note],
  );

  const handleToggleLike = useCallback(() => {
    if (note.viewer_reaction === "like") onClearReaction(note);
    else onReact(note, "like");
  }, [note, onReact, onClearReaction]);

  const handleToggleDislike = useCallback(() => {
    if (note.viewer_reaction === "dislike") onClearReaction(note);
    else onReact(note, "dislike");
  }, [note, onReact, onClearReaction]);

  const replies = note.replies ?? [];
  const hasReplies = replies.length > 0;
  const visibleReplies = showAllReplies ? replies : replies.slice(0, 2);
  const hasMoreReplies = replies.length > 2 && !showAllReplies;

  return (
    <Note.Container
      variant="thread"
      className={isOptimistic ? "pointer-events-none opacity-60" : undefined}
    >
      <Note.Header>
        <div className="flex items-center gap-1.5">
          <AuthorAvatar author={note.author} size={18} />
          <span className="text-sm font-medium text-foreground">
            {authorDisplayName(note.author)}
          </span>
        </div>
        <div className="flex items-center gap-1.5">
          {viaLabel && (
            <span className="inline-flex items-center rounded bg-contrast/8 px-1.5 py-0.5 text-[11px] leading-4 text-contrast/60">
              via {viaLabel}
            </span>
          )}
          <Note.Timestamp>{formatTime(note.created_at)}</Note.Timestamp>
        </div>
      </Note.Header>
      {editing && canEdit ? (
        <EditNoteInput
          initialContent={note.content}
          onSave={handleEdit}
          onCancel={() => setEditing(false)}
          isPending={editPending}
        />
      ) : (
        <Note.Content>
          <MentionText content={note.content} />
        </Note.Content>
      )}
      {!editing && (
        <NoteToolbar
          note={note}
          canEdit={canEdit}
          canWrite={canWrite}
          busy={busy}
          onReply={() => setShowReply((p) => !p)}
          onToggleLike={handleToggleLike}
          onToggleDislike={handleToggleDislike}
          onEdit={canEdit ? () => setEditing(true) : undefined}
          onDelete={canEdit ? () => onDelete(note) : undefined}
        />
      )}

      {hasReplies && !editing && (
        <>
          <Note.Separator />
          <Note.RepliesHeading count={replies.length} />
          {visibleReplies.map((reply) => (
            <ReplyCard
              key={reply.id}
              note={reply}
              canWrite={canWrite}
              currentUserId={currentUserId}
              isOrgAdmin={isOrgAdmin}
              viaLabel={null}
              busy={busy}
              editPending={editPending}
              onReply={onReply}
              onEdit={onEdit}
              onDelete={onDelete}
              onReact={onReact}
              onClearReaction={onClearReaction}
            />
          ))}
          {hasMoreReplies && (
            <Note.SeeAllRepliesButton onClick={() => setShowAllReplies(true)} />
          )}
        </>
      )}

      <AnimatePresence>
        {canWrite && showReply && !editing && (
          <motion.div
            key="reply-input"
            className="flex flex-col gap-3"
            {...REPLY_ANIM}
          >
            {!hasReplies && <Note.Separator />}
            <ReplyInput
              onSubmit={handleReply}
              onCancel={() => setShowReply(false)}
              isPending={busy}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </Note.Container>
  );
}
